import type {
  AssignmentSubmission,
  UpdateStudentSubmissionCorrectionSeenBody,
  UpdateStudentSubmissionSubmitBody,
  UpdateStudentSubmissionUpdateBody,
} from '@didaskill/domain/assignment-submissions/types';
import type {
  ASSIGNMENT_QUESTION_VIEWS,
  ASSIGNMENT_STATUSES,
} from '@didaskill/domain/assignments/constants';
import type {
  McqAnswer,
  McqPageViewCorrection,
  McqPageViewForm,
  StudentAssignment,
} from '@didaskill/domain/assignments/types';
import type { Configuration, Preferences } from '@didaskill/domain/configurations/types';
import type { FEEDBACK_CAMPAIGN_ORIGINS } from '@didaskill/domain/feedback-campaigns/constants';
import type { FeedbackCampaign } from '@didaskill/domain/feedback-campaigns/types';
import type { FlashcardsDeck } from '@didaskill/domain/flashcards-deck/types';
import type { LEGAL_DOCUMENT_TYPES } from '@didaskill/domain/legal-document/constants';
import type { LegalDocument } from '@didaskill/domain/legal-document/types';
import type { GetNewsForStudentQuery, NewsViewStudent } from '@didaskill/domain/news/types';
import type {
  LearningResourceFeedback,
  LearningResourceRating,
  LearningResourceReport,
  PostLearningResourceRatingBody,
  PostLearningResourceReportBody,
} from '@didaskill/domain/notations/types';
import type {
  GetStudentActivitySummaryQuery,
  StudentStatistics,
} from '@didaskill/domain/reporting/types';
import { USER_VIEWS } from '@didaskill/domain/users/constants';
import type {
  CampusSearchResult,
  EmployeeViewFull,
  ExamRegistrationLinkCrm,
  GetCampusSearchQuery,
  GetCurrentUserFeatureFlagsQuery,
  GetCurrentUserQuery,
  PaymentLinkCrm,
  StudentCalendarEvent,
  StudentViewFull,
  StudentViewPersonal,
  StudentViewPublic,
  TutorViewFull,
  UpdateCurrentUserBody,
  UpdateCurrentUserCourseBody,
  UpdateFlashcardsDecksBody,
  UserFeatureFlag,
} from '@didaskill/domain/users/types';
import type {
  GetStudentVirtualClassroomsQuery,
  VirtualClassroomViewMinimal,
} from '@didaskill/domain/virtual-classrooms/types';
import type { MailMetadata, TypedResponse } from '@didaskill/types/misc';

import { HttpRepository } from '../httpRepository';

const e = encodeURIComponent;

const BASE_USERS_URI = '/api/v1/users/users';

export class UsersRepository extends HttpRepository {
  /**
   * Get connected user info.
   *
   * @param options - Options.
   * @param options.view - User view.
   * @returns User object.
   */
  async getCurrentUser({
    view = USER_VIEWS.FULL,
  }: GetCurrentUserQuery = {}): Promise<
    StudentViewFull | StudentViewPersonal | EmployeeViewFull | TutorViewFull
  > {
    const { body: user } = await this.http.get(`${BASE_USERS_URI}/me`).query({ view });

    return user;
  }

  /**
   * Searches campus content available to the authenticated student.
   * @param query Search text and optional current course/class for ranking.
   * @returns Ranked search hits.
   */
  async searchCampus(query: GetCampusSearchQuery): Promise<CampusSearchResult> {
    const { body } = await this.http.get(`${BASE_USERS_URI}/me/search`).query(query);

    return body;
  }

  async getCurrentUserFeatureFlags(
    query?: GetCurrentUserFeatureFlagsQuery,
  ): Promise<UserFeatureFlag[]> {
    const { body: data } = await this.http.get(`${BASE_USERS_URI}/me/feature-flags`).query(query);

    return data;
  }

  /**
   * Update the current user.
   *
   * @param params - Object with user values.
   * @param params.personalEmail - The personal email of the user.
   * @param params.picture - The picture id.
   * @returns The updated user data.
   */
  async updateSelfUser({
    picture,
    isProfilePublic,
    displayName,
  }: UpdateCurrentUserBody): Promise<StudentViewFull | EmployeeViewFull> {
    const { body: user } = await this.http.patch(`${BASE_USERS_URI}/me`).send({
      picture,
      isProfilePublic,
      displayName,
    });

    return user;
  }

  /**
   * Fetch the school certificate blob.
   *
   * @param courseId - Course Id.
   * @param classId - Class id.
   * @returns The blob of the pdf file.
   */
  async getSchoolCertificate(courseId: string, classId: string): Promise<Blob> {
    const { body } = await this.http
      .get(`${BASE_USERS_URI}/me/documents/school-certificate`)
      .query({ courseId, classId })
      .responseType('blob');

    return body;
  }

  /**
   * Fetch the student's academic transcript for a course.
   *
   * @param courseId - Id of the course.
   * @param classId - Id of the class.
   * @param startDate - Start date of the period (course access start date by default).
   * @param endDate - End date of the period (today by default).
   *
   * @returns Transcript data.
   */
  async getCurrentStudentAcademicTranscript(
    courseId: string,
    classId: string,
    startDate?: string,
    endDate?: string,
    // It could be a UserTranscriptArchive also but it's easier to use StudentAcademicTranscript
  ): Promise<Blob> {
    const { body: transcript } = await this.http
      .get('/api/v1/users/users/me/documents/academic-transcript')
      .query({
        courseId,
        classId,
        startDate,
        endDate,
      })
      .responseType('blob');

    return transcript;
  }

  /**
   * Get user's future calendar events.
   *
   * @param query - Query parameters.
   * @param query.limit - Max number of events.
   * @param query.timeMin - Min date of events (iso datetime).
   * @param query.timeMax - Max date of events (iso datetime).
   * @returns A list of events.
   */
  async getUserCalendarEvents({
    limit,
    timeMin,
    timeMax,
  }: {
    limit?: number;
    timeMin?: string;
    timeMax?: string;
  }): Promise<StudentCalendarEvent[]> {
    const { body } = await this.http
      .get(`${BASE_USERS_URI}/me/calendar`)
      .query({ limit, timeMin, timeMax });

    return body;
  }

  /**
   * Get the metadata of the user's mail box.
   *
   * @param labelId - Identify the label used to query Gmail.
   * @returns An object with the metadata of the mail box.
   */
  async getUserMails(labelId: string): Promise<MailMetadata> {
    const { body } = await this.http.get(`${BASE_USERS_URI}/me/mails`).query({ labelId });

    return body;
  }

  /**
   * Get student assignments with submissions.$
   *
   * @param courseId - Course id.
   * @param classId - Class id.
   * @param query - Query.
   * @returns List of assignments.
   */
  async getUserAssignments(
    courseId: string,
    classId: string,
  ): Promise<Record<ASSIGNMENT_STATUSES, { count: number; items: StudentAssignment[] }>> {
    const { body } = await this.http.get(`${BASE_USERS_URI}/me/assignments`).query({
      courseId,
      classId,
    });

    return body;
  }

  /**
   * Get an assignment of a student and its submission if it exists.
   *
   * @param assignmentId - Assignment id.
   * @param courseId - Assignment id.
   * @param classId - Class id.
   * @param teachingUnitId - Teaching unit id.
   * @param lessonId - Lesson id.
   * @returns Assignment.
   */
  async getUserAssignment(
    assignmentId: string,
    courseId: string,
    classId: string,
    teachingUnitId: string,
    lessonId: string | null,
  ): Promise<StudentAssignment> {
    const { body } = await this.http
      .get(`${BASE_USERS_URI}/me/assignments/${e(assignmentId)}`)
      .query({
        courseId,
        classId,
        teachingUnitId,
        lessonId,
      });

    return body;
  }

  /**
   * Update a student's submission.
   *
   * @param submissionId - Submission id.
   * @param params - Data to update.
   * @returns Updated submission.
   */
  async patchUserAssignmentSubmission(
    submissionId: string,
    depositId: string,
    params:
      | UpdateStudentSubmissionSubmitBody
      | UpdateStudentSubmissionCorrectionSeenBody
      | UpdateStudentSubmissionUpdateBody,
  ): Promise<AssignmentSubmission> {
    const { body } = await this.http
      .patch(
        `${BASE_USERS_URI}/me/assignment-submissions/${e(submissionId)}/deposits/${e(depositId)}`,
      )
      .send(params);

    return body;
  }

  /**
   * Get assignment questions.
   *
   * @param assignmentId - Id of the assignment.
   * @param courseId - Id of the course.
   * @param classId - Id of the class.
   * @param teachingUnitId - Id of the teachingUnit.
   * @param lessonId - Id of the lesson.
   * @param view - View.
   * @returns The list of questions.
   */
  async getAssignmentQuestions(
    assignmentId: string,
    courseId: string,
    classId: string,
    teachingUnitId: string,
    lessonId: string | null,
    view?: ASSIGNMENT_QUESTION_VIEWS,
  ): Promise<McqPageViewForm[] | McqPageViewCorrection[]> {
    const { body } = await this.http
      .get(`${BASE_USERS_URI}/me/assignments/${e(assignmentId)}/questions`)
      .query({
        courseId,
        classId,
        teachingUnitId,
        lessonId,
        view,
      });

    return body;
  }

  /**
   * Create a student's submission.
   *
   * @param assignmentId - Assignment id.
   * @param courseId - Course id.
   * @param classId - Class id.
   * @param teachingUnitId - Teaching unit id.
   * @param lessonId - Lesson id.
   * @param params - Params to post.
   * @param params.mcqAnswers - Answers to MCQ.
   * @returns Student's submission.
   */
  async createAssignmentSubmission(
    assignmentId: string,
    courseId: string,
    classId: string,
    teachingUnitId: string,
    lessonId?: string | null,
    {
      mcqAnswers = undefined,
    }: {
      mcqAnswers?: McqAnswer[];
    } = {},
  ): Promise<AssignmentSubmission> {
    const { body } = await this.http
      .post(`${BASE_USERS_URI}/me/assignments/${e(assignmentId)}/assignment-submissions`)
      .send({
        courseId,
        classId,
        teachingUnitId,
        lessonId,
        mcqAnswers,
      });

    return body;
  }

  /**
   * Student signin to a virtual classroom, accepting or denying image rights.
   *
   * @param virtualClassroomId Virtual classroom id.
   * @param acceptedImageRights - Image rights waiver status.
   * @returns Success status.
   */
  async signInToVirtualClassroom(
    virtualClassroomId: string,
    acceptedImageRights: boolean,
  ): Promise<boolean> {
    const { status } = await this.http
      .post(`${BASE_USERS_URI}/me/virtual-classrooms/${e(virtualClassroomId)}/signin`)
      .send({
        acceptedImageRights,
      });

    return status === 204;
  }

  /**
   * Update the student course information.
   *
   * @param courseId - Course id.
   * @param classId - Class id.
   * @param updates - Course updates.
   * @param updates.needsOnboardingMeeting - Set onboarding meeting status.
   * @param updates.firstConnection - Set first connection date on course.
   * @param updates.studentOnboardingTourDone - Set onboarding tour done status.
   * @returns Updated student object.
   */
  async updateStudentCourse(
    courseId: string,
    classId: string,
    {
      needsOnboardingMeeting,
      firstConnection,
      studentOnboardingTourDone,
    }: UpdateCurrentUserCourseBody,
  ): Promise<StudentViewFull> {
    const { body } = await this.http
      .patch(`${BASE_USERS_URI}/me/courses/${e(courseId)}/${e(classId)}`)
      .send({
        needsOnboardingMeeting,
        firstConnection,
        studentOnboardingTourDone,
      });

    return body;
  }

  /**
   * Get the legal documents required by the plans of the current student's courses.
   *
   * @returns Legal documents.
   */
  async getStudentLegalDocuments(): Promise<LegalDocument[]> {
    const { body } = await this.http.get(`${BASE_USERS_URI}/me/legal-documents`);

    return body;
  }

  /**
   * Accept a legal document.
   *
   * @param legalDocumentType - LEGAL_DOCUMENT_TYPES enum.
   * @returns Updated student object.
   */
  async acceptLegalDocument(legalDocumentType: LEGAL_DOCUMENT_TYPES): Promise<StudentViewFull> {
    const { body } = await this.http.put(
      `${BASE_USERS_URI}/me/legal-documents/${e(legalDocumentType)}`,
    );

    return body;
  }

  async deleteProfilePicture(pictureId: string): Promise<StudentViewFull | EmployeeViewFull> {
    const { body: updatedUser } = await this.http.delete(
      `${BASE_USERS_URI}/me/profile-pictures/${e(pictureId)}`,
    );

    return updatedUser;
  }

  /**
   * Get the list of virtual classrooms.
   *
   * @param query - Query.
   * @returns List of virtual classrooms.
   */
  async getVirtualClassrooms(query: GetStudentVirtualClassroomsQuery): Promise<{
    count: number;
    virtualClassrooms: VirtualClassroomViewMinimal[];
  }> {
    const { body } = await this.http.get(`${BASE_USERS_URI}/me/virtual-classrooms`).query(query);

    return body;
  }

  /**
   * Fetch current user payment link from CRM.
   *
   * @param courseId - Id of the course.
   * @param classId - Id of the class.
   * @returns Payment link.
   */
  async getPaymentLink(courseId: string, classId: string): Promise<PaymentLinkCrm> {
    const { body } = await this.http.get(`${BASE_USERS_URI}/me/payment-link`).query({
      courseId,
      classId,
    });

    return body;
  }

  /**
   * Fetch current user exam registration link from CRM.
   *
   * @param courseId - Id of the course.
   * @param classId - Id of the class.
   * @returns Exam registration link.
   */
  async getExamRegistrationLink(
    courseId: string,
    classId: string,
  ): Promise<ExamRegistrationLinkCrm> {
    const { body } = await this.http.get(`${BASE_USERS_URI}/me/exam-registration-link`).query({
      courseId,
      classId,
    });

    return body;
  }

  /**
   * Create a new deposit in assignment submission.
   *
   * @param assignmentId - Assignment id.
   * @param submissionId - Submission id.
   * @returns Student's submission.
   */
  async createDeposit(assignmentId: string, submissionId: string): Promise<AssignmentSubmission> {
    const { body } = await this.http.post(
      `${BASE_USERS_URI}/me/assignments/${e(
        assignmentId,
      )}/assignment-submissions/${e(submissionId)}/deposits`,
    );

    return body;
  }

  /**
   * Send student picture to get student card.
   *
   * @param courseId - Course id.
   * @param classId - Class id.
   * @param params - Params.
   * @param params.file - Student picture.
   */
  async postStudentCardPicture(courseId: string, classId: string, { file }: { file: Blob }) {
    const { status } = await this.http
      .post(`${BASE_USERS_URI}/me/courses/${e(courseId)}/${e(classId)}/student-card`)
      // biome-ignore lint/suspicious/noExplicitAny: DOM Blob type is not compatible with Node.js Blob type expected by superagent
      .attach('file', file as any)
      .set('enctype', 'multipart/form-data');

    return status;
  }

  /**
   * Fetch the student card download url.
   *
   * @param courseId - Course Id.
   * @param classId - Class id.
   * @returns The CRM url to download student card.
   */
  async getStudentCardLink(courseId: string, classId: string) {
    const { body } = await this.http.get(
      `${BASE_USERS_URI}/me/courses/${e(courseId)}/${e(classId)}/student-card/link`,
    );

    return body;
  }

  /**
   * Get the student feedback campaigns.
   *
   * @param courseId - Course Id.
   * @param classId - Class id.
   * @returns List of feedback campaigns.
   */
  async getStudentFeedbackCampaigns(
    courseId: string,
    classId: string,
  ): Promise<FeedbackCampaign[]> {
    const { body } = await this.http.get(
      `${BASE_USERS_URI}/me/courses/${e(courseId)}/${e(classId)}/feedback-campaigns`,
    );

    return body;
  }

  /**
   * Post answer for a feedback campaign.
   *
   * @param courseId - Course Id.
   * @param classId - Class id.
   * @param campaignId - Campaign Id.
   * @param rating - Rating of the feedback.
   * @param comment - Comment for the feedback.
   * @returns List of feedback campaigns.
   */
  async postStudentFeedbackCampaignAnswers(
    courseId: string,
    classId: string,
    campaignId: string,
    origin: FEEDBACK_CAMPAIGN_ORIGINS,
    rating?: number,
    comment?: string,
    externalAnswerId?: string,
    context?: Record<string, string>,
  ): Promise<FeedbackCampaign[]> {
    const { body } = await this.http
      .post(
        `${BASE_USERS_URI}/me/courses/${e(courseId)}/${e(
          classId,
        )}/feedback-campaigns/${campaignId}/answers`,
      )
      .send({
        rating,
        comment,
        externalAnswerId,
        origin,
        context,
      });

    return body;
  }

  /**
   * Fetch the student news.
   *
   * @param courseId - Course Id.
   * @param classId - Class id.
   * @param query - Query
   * @returns The news.
   */
  async getStudentNews(
    courseId: string,
    classId: string,
    query?: GetNewsForStudentQuery,
  ): Promise<{ count: number; news: NewsViewStudent[] }> {
    const { body } = await this.http.get(`${BASE_USERS_URI}/me/news`).query({
      courseId,
      classId,
      ...query,
    });

    return body;
  }

  /**
   * Update student favorite news
   *
   * @param newsId - News Id.
   * @param isFavorite - New isFavorite value for the news
   * @returns The news.
   */
  async updateStudentFavoriteNews(newsId: string, isFavorite: boolean): Promise<void> {
    const { body } = await this.http.put(`${BASE_USERS_URI}/me/news/${e(newsId)}/favorite`).send({
      isFavorite,
    });

    return body;
  }

  /**
   * Get the student activity summary.
   *
   * @param startDate - Start date of the dates range.
   * @param endDate - End date of the dates range.
   * @returns The running streak at endDate.
   */
  async getStudentActivitySummary({ startDate, endDate }: GetStudentActivitySummaryQuery): Promise<{
    streak: number;
    connectionDays: string[];
  }> {
    const { body } = await this.http.get(`${BASE_USERS_URI}/me/activity-summary`).query({
      startDate,
      endDate,
    });

    return body;
  }

  /*
   * Register the current student to a virtual classroom.
   *
   * @param virtualClassroomId - Virtual classroom id.
   */
  async registerToVirtualClassroom(virtualClassroomId: string): Promise<void> {
    await this.http.post(
      `${BASE_USERS_URI}/me/virtual-classrooms/${e(virtualClassroomId)}/registration`,
    );
  }

  /**
   * Unregister the current student from a virtual classroom.
   * @param virtualClassroomId - Virtual classroom id.
   */
  async unregisterFromVirtualClassroom(virtualClassroomId: string): Promise<void> {
    await this.http.delete(
      `${BASE_USERS_URI}/me/virtual-classrooms/${e(virtualClassroomId)}/registration`,
    );
  }

  /**
   * Get the student statistics.
   * @param courseId - Course id.
   * @param classId - Class id.
   * @returns The student statistics.
   */
  async getStatistics(query: {
    courseId: string;
    classId: string;
  }): Promise<TypedResponse<StudentStatistics>> {
    const res = await this.http.get(`${BASE_USERS_URI}/me/statistics`).query(query);

    return res;
  }

  /**
   * Get the student flashcards decks.
   * @param courseId - Course id.
   * @param classId - Class id.
   * @returns The student flashcards decks.
   */
  async getFlashcardsDecks(query: {
    courseId: string;
    classId: string;
  }): Promise<TypedResponse<FlashcardsDeck[]>> {
    const res = await this.http.get(`${BASE_USERS_URI}/me/flashcards-decks`).query(query);

    return res;
  }

  /**
   * Create a flashcards deck.
   * @param courseId - Course id.
   * @param classId - Class id.
   * @param title - Deck title.
   * @returns The flashcards deck.
   */
  async postFlashcardsDeck(
    courseId: string,
    classId: string,
    title: string,
  ): Promise<TypedResponse<FlashcardsDeck>> {
    const res = await this.http.post(`${BASE_USERS_URI}/me/flashcards-decks`).send({
      courseId,
      classId,
      title,
    });

    return res;
  }

  /**
   * Update a student's flashcards deck.
   * @param deckId - Deck id.
   * @returns Updated flashcard deck.
   * */
  async updateFlashcardsDeck(
    deckId: string,
    body: Partial<UpdateFlashcardsDecksBody>,
  ): Promise<TypedResponse<FlashcardsDeck>> {
    const res = await this.http.patch(`${BASE_USERS_URI}/me/flashcards-decks/${deckId}`).send(body);

    return res;
  }

  /**
   * Delete a student's flashcards deck.
   * @param deckId - Deck id.
   * @returns No-content status.
   * */
  async removeFlashcardsDeck(deckId: string): Promise<TypedResponse<void>> {
    const res = await this.http.delete(`${BASE_USERS_URI}/me/flashcards-decks/${deckId}`);

    return res;
  }

  /**
   * Update the student's understanding of a card.
   * @param deckId - Deck id.
   * @param cardId - Card id.
   * @param isUnderstood - Understanding status.
   * @returns No-content status.
   */
  async updateFlashcardUnderstandingStatus(
    deckId: string,
    cardId: string,
    isUnderstood: boolean,
  ): Promise<TypedResponse<FlashcardsDeck>> {
    const res = await this.http
      .put(`${BASE_USERS_URI}/me/flashcards-decks/${deckId}/cards/${cardId}`)
      .send({ isUnderstood });

    return res;
  }

  /**
   * Get the effective user configurations for a specific course and class.
   * @param courseId - Course id.
   * @param classId - Class id.
   * @returns The user configurations.
   */
  async getCurrentUserConfigurations(courseId: string, classId: string): Promise<Configuration> {
    const { body } = await this.http.get(`${BASE_USERS_URI}/me/configurations`).query({
      courseId,
      classId,
    });

    return body;
  }

  /**
   * Update the user's preferences configurations.
   * @param body - The body of the request.
   * @returns The updated preferences configurations.
   */
  async updateUserPreferences(body: Partial<Preferences>): Promise<Preferences> {
    const { body: updatedPreferences } = await this.http
      .patch(`${BASE_USERS_URI}/me/configurations/preferences`)
      .send(body);

    return updatedPreferences;
  }

  /**
   * Get the user's preferences configurations.
   * @returns The user's preferences configurations.
   */
  async getUserPreferences(): Promise<Preferences> {
    const { body } = await this.http.get(`${BASE_USERS_URI}/me/configurations/preferences`);

    return body;
  }

  /**
   * Get the student's classmates for a specific course and class.
   * @param courseId - Course id.
   * @param classId - Class id.
   * @returns The user's classmates.
   */
  async getCurrentUserClassmates(
    courseId: string,
    classId: string,
    query?: {
      sort?: string;
      limit?: number;
      offset?: number;
      excludeViewer?: boolean;
    },
  ): Promise<{ count: number; students: StudentViewPublic[] }> {
    const { body } = await this.http.get(`${BASE_USERS_URI}/me/classmates`).query({
      courseId,
      classId,
      ...query,
    });

    return body;
  }

  /**
   * Assigns the appropriate Discord role to the current user
   * for a specific course and class.
   *
   * This endpoint triggers the backend logic responsible for:
   * - Linking the authenticated user to Discord (if not already linked)
   * - Determining the correct Discord role based on the course and class
   * - Assigning that role to the user in the Discord guild
   *
   * @param courseId - Course id.
   * @param classId - Class id.
   *
   * @returns Resolves when the Discord role has been successfully assigned.
   */
  async addDiscordRoleToUser(courseId: string, classId: string): Promise<void> {
    await this.http.post(`${BASE_USERS_URI}/me/discord/role`).query({
      courseId,
      classId,
    });
  }

  /**
   * Gets the student's rating and report for a learning resource.
   * @param params Learning resource identifier and course/class context.
   * @returns Existing feedback for the resource.
   */
  async getLearningResourceFeedback({
    learningResourceId,
    courseId,
    classId,
  }: {
    learningResourceId: string;
    courseId: string;
    classId: string;
  }): Promise<LearningResourceFeedback> {
    const { body } = await this.http
      .get(`${BASE_USERS_URI}/me/learning-resources/${e(learningResourceId)}/feedback`)
      .query({ courseId, classId });

    return body;
  }

  /**
   * Posts a binary rating for a learning resource.
   * @param params Learning resource, course/class context and rating payload.
   * @returns The created learning resource rating.
   */
  async postLearningResourceRating({
    learningResourceId,
    courseId,
    classId,
    rating,
  }: {
    learningResourceId: string;
    courseId: string;
    classId: string;
  } & PostLearningResourceRatingBody): Promise<LearningResourceRating> {
    const { body } = await this.http
      .post(`${BASE_USERS_URI}/me/learning-resources/${e(learningResourceId)}/rating`)
      .query({ courseId, classId })
      .send({ rating });

    return body;
  }

  /**
   * Deletes the student's rating for a learning resource.
   * @param params Learning resource identifier and course/class context.
   * @returns void
   */
  async deleteLearningResourceRating({
    learningResourceId,
    courseId,
    classId,
  }: {
    learningResourceId: string;
    courseId: string;
    classId: string;
  }): Promise<void> {
    await this.http
      .delete(`${BASE_USERS_URI}/me/learning-resources/${e(learningResourceId)}/rating`)
      .query({ courseId, classId });
  }

  /**
   * Posts a report for a learning resource.
   * @param params Learning resource, course/class context and report payload.
   * @returns The created learning resource report.
   */
  async postLearningResourceReport({
    learningResourceId,
    courseId,
    classId,
    reason,
    comment,
  }: {
    learningResourceId: string;
    courseId: string;
    classId: string;
  } & PostLearningResourceReportBody): Promise<LearningResourceReport> {
    const { body } = await this.http
      .post(`${BASE_USERS_URI}/me/learning-resources/${e(learningResourceId)}/report`)
      .query({ courseId, classId })
      .send({ reason, comment });

    return body;
  }

  /**
   * Updates an existing report for a learning resource.
   * @param params Learning resource, course/class context and report payload.
   * @returns The updated learning resource report.
   */
  async patchLearningResourceReport({
    learningResourceId,
    courseId,
    classId,
    reason,
    comment,
  }: {
    learningResourceId: string;
    courseId: string;
    classId: string;
  } & PostLearningResourceReportBody): Promise<LearningResourceReport> {
    const { body } = await this.http
      .patch(`${BASE_USERS_URI}/me/learning-resources/${e(learningResourceId)}/report`)
      .query({ courseId, classId })
      .send({ reason, comment });

    return body;
  }

  /**
   * Deletes the student's report for a learning resource.
   * @param params Learning resource identifier and course/class context.
   * @returns void
   */
  async deleteLearningResourceReport({
    learningResourceId,
    courseId,
    classId,
  }: {
    learningResourceId: string;
    courseId: string;
    classId: string;
  }): Promise<void> {
    await this.http
      .delete(`${BASE_USERS_URI}/me/learning-resources/${e(learningResourceId)}/report`)
      .query({ courseId, classId });
  }
}
